import React, { FC } from 'react'
import { Typography, Flex, Row, Col, Avatar, Dropdown, message } from 'antd'
import { CopyOutlined, MoreOutlined } from '@ant-design/icons'
import type { RevenueSource } from 'store/verticals/types'
import { formatDate } from 'containers/VerticalPage/common/ActivityOverviewCard/utils'

const nftBadge = require('../assets/nft-badge.svg')
const walletBadge = require('../assets/wallet-badge.svg')
const etherscanIcon = require('components/BusinessSource/assets/original/etherscan.svg')

import { SectionCard, formatAbbreviatedCurrency } from 'containers/VerticalPage/common/antd'

const { Text } = Typography

const STAT_LABEL: React.CSSProperties = {
  fontFamily: 'Roboto',
  fontSize: 12,
  color: '#8c8c8c',
  display: 'block',
  lineHeight: '18px',
}

const STAT_VALUE: React.CSSProperties = {
  fontFamily: 'Roboto',
  fontSize: 14,
  fontWeight: 500,
  display: 'block',
  lineHeight: '22px',
}

const getPlatformKey = (type?: string): string => {
  if (!type) return 'wallet'
  const lower = type.toLowerCase()
  if (lower === 'smart contract') return 'smartcontract'
  return lower
}

interface RevenueSourceCardProps {
  source: RevenueSource
}

const BADGE_MAP: Record<string, string> = {
  wallet: walletBadge,
  smartcontract: nftBadge,
  ethereum: walletBadge,
  nft: nftBadge,
}

interface PlatformLinkConfig {
  key: string
  label: string
  icon: string
  getUrl: (address: string, profileUrl?: string) => string
}

const ETHEREUM_PLATFORM_LINKS: PlatformLinkConfig[] = [
  {
    key: 'etherscan',
    label: 'Etherscan',
    icon: etherscanIcon,
    getUrl: (_address, profileUrl) => profileUrl || '',
  },
]

const PLATFORM_ICON_STYLE: React.CSSProperties = {
  width: 12,
  height: 12,
  borderRadius: '50%',
  objectFit: 'cover',
  flexShrink: 0,
}

const RevenueSourceCard: FC<RevenueSourceCardProps> = ({ source }) => {
  const platformKey = getPlatformKey(source.overview?.type)
  const displayName = source.overview?.alias || 'No Known Alias'
  const badgeIcon = BADGE_MAP[platformKey] || BADGE_MAP[source.platform] || walletBadge

  const walletAddress = source.profileName || source.profileUrl

  const handleCopy = () => {
    navigator.clipboard.writeText(walletAddress)
    message.success('Address copied')
  }

  const address = source.profileName

  const platformLinksOverlay = address ? (
    <div
      style={{
        width: 189,
        borderRadius: 2,
        padding: '0',
        background: '#fff',
        boxShadow: '0 3px 6px -4px rgba(0,0,0,.12), 0 6px 16px 0 rgba(0,0,0,.08), 0 9px 28px 8px rgba(0,0,0,.05)',
      }}
    >
      <div
        style={{
          fontFamily: 'Roboto',
          fontWeight: 400,
          fontSize: 14,
          color: '#000000D9',
          padding: '10px 12px',
        }}
      >
        Platforms Links
      </div>
      {ETHEREUM_PLATFORM_LINKS.map(p => (
        <a
          key={p.key}
          href={p.getUrl(address, source.profileUrl)}
          target='_blank'
          rel='noreferrer'
          className='platform-link-option'
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '10px 24px',
            fontFamily: 'Roboto',
            fontWeight: 400,
            fontSize: 14,
            color: '#1890FF',
            textDecoration: 'none',
            borderRadius: 2,
          }}
        >
          <img src={p.icon} alt={p.label} style={PLATFORM_ICON_STYLE} />
          {p.label}
        </a>
      ))}
    </div>
  ) : null

  const formatValue = (val: number | null | undefined): string => {
    if (val == null) return 'N/A'
    return formatAbbreviatedCurrency(val, 1)
  }

  const stats = [
    {
      label: 'Blockchain',
      value: (source.overview?.chains?.join(', ') || 'ethereum').replace(/\b\w/g, c => c.toUpperCase()),
    },
    { label: 'Inflows', value: formatValue(source.overview?.inflows) },
    { label: 'Outflows', value: formatValue(source.overview?.outflows) },
    {
      label: 'First Activity',
      value: source.overview?.firstActivity ? formatDate(source.overview.firstActivity) : 'N/A',
    },
  ]

  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 0,
        border: '1px solid #D9D9D9',
        padding: 16,
      }}
    >
      <Flex justify='space-between' align='flex-start' style={{ marginBottom: 12 }}>
        <Flex gap={12} align='center'>
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <Avatar src={source.profileImageUrl} size={50}>
              {displayName?.[0]}
            </Avatar>
            <img
              src={badgeIcon}
              alt={platformKey}
              style={{
                width: 22,
                height: 22,
                position: 'absolute',
                bottom: -3,
                right: -3,
              }}
            />
          </div>
          <div style={{ minWidth: 0 }}>
            <Text
              style={{
                fontFamily: 'Roboto',
                fontWeight: 500,
                fontSize: 14,
                lineHeight: '22px',
                color: '#1890FF',
                display: 'block',
              }}
              ellipsis
            >
              {displayName}
            </Text>
            <Flex align='center' gap={4}>
              <Text
                type='secondary'
                style={{ fontSize: 12, fontFamily: 'monospace', cursor: 'pointer' }}
                onClick={handleCopy}
                ellipsis
              >
                {walletAddress}
              </Text>
              <CopyOutlined
                style={{ color: '#000000D9', cursor: 'pointer', fontSize: 12, flexShrink: 0 }}
                onClick={handleCopy}
              />
            </Flex>
          </div>
        </Flex>

        {platformLinksOverlay && (
          <Dropdown dropdownRender={() => platformLinksOverlay} trigger={['click']} placement='bottomRight'>
            <MoreOutlined style={{ fontSize: 18, cursor: 'pointer', color: '#000000D9', paddingRight: 14 }} />
          </Dropdown>
        )}
      </Flex>

      <Flex justify='space-between' style={{ paddingTop: 8, paddingRight: 60 }}>
        {stats.map(s => (
          <div key={s.label}>
            <Text style={STAT_LABEL}>{s.label}</Text>
            <Text style={STAT_VALUE}>{s.value}</Text>
          </div>
        ))}
      </Flex>
    </div>
  )
}

interface RevenueSourceCardsProps {
  revenueSources: RevenueSource[]
}

const RevenueSourceCards: FC<RevenueSourceCardsProps> = ({ revenueSources }) => {
  if (!revenueSources?.length) return null

  return (
    <>
      <style>{`.platform-link-option:hover { background: #F5F5F5; }`}</style>
      <SectionCard title='Revenue Sources' style={{ background: 'transparent', border: 'none', padding: 0 }}>
        <Row gutter={[12, 12]}>
          {revenueSources.map(source => (
            <Col xs={24} md={12} key={source.profileUrl}>
              <RevenueSourceCard source={source} />
            </Col>
          ))}
        </Row>
      </SectionCard>
    </>
  )
}

export default RevenueSourceCards
